import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import {
    Alert,
    Box,
    BottomNavigation,
    BottomNavigationAction,
    Button,
    Chip,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import { getUserDetails } from "../../redux/userRelated/userHandle";
import CustomBarChart from "../../components/CustomBarChart";
import { getApiBaseUrl } from "../../config/api";
import { getMalawiGrade } from "../../utils/gradingUtils";
import {
    allowedReportPeriods,
    CONTINUOUS_ASSESSMENT_PERIOD,
    STUDENT_PERIOD_TABS,
} from "../../utils/reportPeriodUtils";
import {
    averageFromResultRows,
    formatArchivedTermLabel,
    hasAnyPeriodResults,
    resolveStudentResultsByPeriod,
} from "../../utils/studentResultsByPeriod";
import { ResultsPageContainer, ResultsPageHeader, KpiCard, ResultsSectionPaper, ResponsiveTableWrap } from "../admin/examRelated/resultsUi";

const PERIOD_TABS = STUDENT_PERIOD_TABS;

function rowKey(row, index) {
    return `${row.subjectName}-${row.examLabel || row.reportPeriod || ""}-${index}`;
}

function MarksTable({ periodKey, periodMarks }) {
    return (
        <ResponsiveTableWrap>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Subject</TableCell>
                        {periodKey === "classTests" ? <TableCell>Test</TableCell> : null}
                        <TableCell align="right">Marks</TableCell>
                        <TableCell align="center">Grade</TableCell>
                        <TableCell>Status</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {periodMarks.map((row, i) => (
                        <TableRow key={rowKey(row, i)}>
                            <TableCell>{row.subName}</TableCell>
                            {periodKey === "classTests" ? (
                                <TableCell>{row.examLabel || "Class test"}</TableCell>
                            ) : null}
                            <TableCell align="right">{row.marksObtained}</TableCell>
                            <TableCell align="center">
                                <Chip label={row.grade} size="small" color={row.passed ? "success" : "error"} />
                            </TableCell>
                            <TableCell>{row.passed ? "Pass" : "Fail"}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </ResponsiveTableWrap>
    );
}

const StudentResults = () => {
    const dispatch = useDispatch();
    const { userDetails, currentUser, loading } = useSelector((s) => s.user);
    const [viewMode, setViewMode] = useState("current");
    const [section, setSection] = useState("table");
    const [periodKey, setPeriodKey] = useState("endOfTerm");
    const [reportCard, setReportCard] = useState(null);
    const [archivedCard, setArchivedCard] = useState(null);
    const [selectedArchiveIndex, setSelectedArchiveIndex] = useState(0);
    const [loadError, setLoadError] = useState("");

    const studentId = currentUser?._id;
    const educationLevel = reportCard?.educationLevel || archivedCard?.educationLevel || userDetails?.educationLevel || "Secondary";
    const isUniversity = educationLevel === "University";

    const visibleTabs = useMemo(() => {
        const allowed = new Set(["Class test", ...allowedReportPeriods(educationLevel)]);
        return PERIOD_TABS.filter((tab) => allowed.has(tab.reportPeriod));
    }, [educationLevel]);

    useEffect(() => {
        if (studentId) dispatch(getUserDetails(studentId, "Student"));
    }, [dispatch, studentId]);

    useEffect(() => {
        if (!studentId || isUniversity) return;
        let cancelled = false;
        axios
            .get(`${getApiBaseUrl()}/StudentReportCard/${studentId}?portal=true`)
            .then(({ data }) => {
                if (!cancelled) setReportCard(data);
            })
            .catch(() => {
                if (!cancelled) setLoadError("Could not load current results.");
            });
        return () => {
            cancelled = true;
        };
    }, [studentId, isUniversity]);

    useEffect(() => {
        if (!studentId || isUniversity) return;
        let cancelled = false;
        axios
            .get(`${getApiBaseUrl()}/StudentReportCard/${studentId}?portal=true&archived=true`)
            .then(({ data }) => {
                if (!cancelled) setArchivedCard(data);
            })
            .catch(() => {
                if (!cancelled) setArchivedCard({ archivedTerms: [] });
            });
        return () => {
            cancelled = true;
        };
    }, [studentId, isUniversity]);

    const archivedTerms = useMemo(
        () => (Array.isArray(archivedCard?.archivedTerms) ? archivedCard.archivedTerms : []),
        [archivedCard],
    );

    const resultsByPeriod = useMemo(() => {
        if (viewMode === "records") {
            const term = archivedTerms[selectedArchiveIndex];
            return term?.resultsByPeriod || { classTests: [], midTerm: [], endOfTerm: [] };
        }
        return resolveStudentResultsByPeriod({
            reportCard,
            userDetails,
            portalOnly: true,
        });
    }, [viewMode, archivedTerms, selectedArchiveIndex, reportCard, userDetails]);

    useEffect(() => {
        if (!resultsByPeriod) return;
        const order = visibleTabs.map((tab) => tab.key);
        const firstWithMarks = order.find((key) => (resultsByPeriod[key] || []).length > 0);
        if (firstWithMarks) setPeriodKey(firstWithMarks);
    }, [resultsByPeriod, visibleTabs, viewMode, selectedArchiveIndex]);

    const periodMarks = useMemo(() => {
        const rows = resultsByPeriod?.[periodKey] || [];
        return rows.map((r) => ({
            subName: r.subjectName,
            examLabel: r.examLabel,
            marksObtained: Number(r.marksObtained) || 0,
            grade: r.grade,
            passed: r.passed,
        }));
    }, [resultsByPeriod, periodKey]);

    const average = useMemo(() => {
        if (viewMode === "records") {
            const term = archivedTerms[selectedArchiveIndex];
            return term?.averageMarkByPeriod?.[periodKey] ?? averageFromResultRows(resultsByPeriod?.[periodKey]);
        }
        return reportCard?.averageMarkByPeriod?.[periodKey] ?? averageFromResultRows(resultsByPeriod?.[periodKey]);
    }, [viewMode, archivedTerms, selectedArchiveIndex, periodKey, reportCard, resultsByPeriod]);

    const classPosition = viewMode === "records" || periodKey === "classTests"
        ? null
        : periodKey === "midTerm"
          ? reportCard?.classPositionByPeriod?.midTerm
          : reportCard?.classPositionByPeriod?.endOfTerm;

    const overall = getMalawiGrade(
        average,
        educationLevel,
        reportCard?.levelCode || archivedCard?.levelCode || userDetails?.sclassName?.levelCode,
        reportCard?.sclassName ||
            archivedCard?.sclassName ||
            userDetails?.sclassName?.sclassName ||
            (typeof userDetails?.sclassName === "string" ? userDetails.sclassName : "")
    );

    const hasAnyCurrent = useMemo(
        () => hasAnyPeriodResults(resolveStudentResultsByPeriod({ reportCard, userDetails, portalOnly: true })),
        [reportCard, userDetails],
    );

    if (loading && !userDetails) {
        return (
            <ResultsPageContainer maxWidth="md">
                <Typography>Loading…</Typography>
            </ResultsPageContainer>
        );
    }

    return (
        <ResultsPageContainer maxWidth="md">
            <ResultsPageHeader
                eyebrow="Student"
                title="My Results"
                subtitle={
                    isUniversity
                        ? "View your semester grades and transcript."
                        : viewMode === "records"
                          ? "Past term marks kept after end-of-term approval."
                          : `Current term: class tests, ${CONTINUOUS_ASSESSMENT_PERIOD.toLowerCase()}, and end-of-term marks.`
                }
                action={
                    isUniversity ? (
                        <Button
                            component={RouterLink}
                            to="/Student/transcript"
                            variant="contained"
                            startIcon={<ArticleOutlinedIcon />}
                        >
                            Transcript
                        </Button>
                    ) : (
                        <Button
                            component={RouterLink}
                            to="/Student/report-card"
                            variant="contained"
                            startIcon={<ArticleOutlinedIcon />}
                        >
                            Report card
                        </Button>
                    )
                }
            />

            {!isUniversity ? (
                <Tabs
                    value={viewMode}
                    onChange={(_, value) => setViewMode(value)}
                    sx={{ mb: 2 }}
                >
                    <Tab value="current" label="Current term" />
                    <Tab
                        value="records"
                        label="Past records"
                        icon={archivedTerms.length ? <Chip size="small" label={archivedTerms.length} sx={{ height: 20, fontSize: "0.7rem" }} /> : undefined}
                        iconPosition="end"
                    />
                </Tabs>
            ) : null}

            {viewMode === "current" && !isUniversity && reportCard && !hasAnyCurrent ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                    No current results yet. Class tests appear after your form teacher accepts them.
                    {CONTINUOUS_ASSESSMENT_PERIOD} and end-of-term marks appear here after director approval. Older terms move to{" "}
                    <strong>Past records</strong> when the school advances to the next term.
                </Alert>
            ) : null}

            {viewMode === "records" && !isUniversity && archivedTerms.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }} icon={<HistoryOutlinedIcon />}>
                    No archived results yet. Completed terms appear here after the school moves to the next term.
                </Alert>
            ) : null}

            {loadError ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {loadError}
                </Alert>
            ) : null}

            {viewMode === "records" && archivedTerms.length > 0 ? (
                <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Archived term</InputLabel>
                    <Select
                        label="Archived term"
                        value={selectedArchiveIndex}
                        onChange={(e) => setSelectedArchiveIndex(Number(e.target.value))}
                    >
                        {archivedTerms.map((term, index) => (
                            <MenuItem key={`${term.term}-${term.academicYear}-${index}`} value={index}>
                                {formatArchivedTermLabel(term)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            ) : null}

            {!isUniversity && visibleTabs.length > 1 ? (
                <Tabs
                    value={periodKey}
                    onChange={(_, value) => setPeriodKey(value)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ mb: 2 }}
                >
                    {visibleTabs.map((tab) => (
                        <Tab
                            key={tab.key}
                            value={tab.key}
                            label={tab.label}
                            icon={
                                (resultsByPeriod?.[tab.key] || []).length ? (
                                    <Chip
                                        size="small"
                                        label={(resultsByPeriod[tab.key] || []).length}
                                        sx={{ height: 20, fontSize: "0.7rem" }}
                                    />
                                ) : undefined
                            }
                            iconPosition="end"
                        />
                    ))}
                </Tabs>
            ) : null}

            {!isUniversity && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} sm={3}>
                        <KpiCard title="Average" value={`${Number(average).toFixed(1)}%`} color="primary" />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <KpiCard title="Grade" value={overall.grade} color={overall.passed ? "success" : "error"} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <KpiCard
                            title="Position"
                            value={classPosition != null ? `#${classPosition}` : "—"}
                            color="info"
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <KpiCard title="Subjects" value={periodMarks.length} color="warning" />
                    </Grid>
                </Grid>
            )}

            {periodMarks.length === 0 ? (
                <Alert severity="info">
                    {viewMode === "records"
                        ? "No marks in this period for the selected archived term."
                        : "No marks for this period yet. Check another tab or ask your teacher."}
                </Alert>
            ) : (
                <ResultsSectionPaper
                    title={
                        viewMode === "records"
                            ? `${formatArchivedTermLabel(archivedTerms[selectedArchiveIndex])} · ${visibleTabs.find((tab) => tab.key === periodKey)?.label || "Marks"}`
                            : visibleTabs.find((tab) => tab.key === periodKey)?.label || "Subject marks"
                    }
                >
                    {section === "table" && <MarksTable periodKey={periodKey} periodMarks={periodMarks} />}
                    {section === "chart" && (
                        <Box sx={{ height: 280 }}>
                            <CustomBarChart
                                chartData={periodMarks.map((m) => ({
                                    subName: {
                                        subName: periodKey === "classTests" && m.examLabel
                                            ? `${m.subName} (${m.examLabel})`
                                            : m.subName,
                                    },
                                    marksObtained: m.marksObtained,
                                }))}
                                dataKey="marksObtained"
                            />
                        </Box>
                    )}
                </ResultsSectionPaper>
            )}

            <Paper sx={{ position: "fixed", bottom: 0, left: 0, right: 0, display: { xs: "block", md: "none" }, zIndex: 10 }} elevation={3}>
                <BottomNavigation value={section} onChange={(_, v) => setSection(v)} showLabels>
                    <BottomNavigationAction label="Table" value="table" icon={<TableChartOutlinedIcon />} />
                    <BottomNavigationAction label="Chart" value="chart" icon={<InsertChartOutlinedIcon />} />
                </BottomNavigation>
            </Paper>
            <Box sx={{ pb: { xs: 8, md: 0 } }} />
        </ResultsPageContainer>
    );
};

export default StudentResults;
